import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function now() {
  return Date.now();
}

function safeStorageGet(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function safeStorageSet(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage failures
  }
}

export function useBotGuards(options = {}) {
  const {
    minFillTimeMs = 4000,
    maxFailures = 5,
    lockoutMs = 10 * 60 * 1000,
    attemptWindowMs = 15 * 60 * 1000,
    storageKey = "bot-guards:v1",
  } = options;

  const mountedAtRef = useRef(now());
  const [honeypot, setHoneypot] = useState("");
  const [submitCount, setSubmitCount] = useState(0);

  const [state, setState] = useState(() =>
    safeStorageGet(storageKey, {
      failures: [],
      lockedUntil: 0,
    }),
  );

  useEffect(() => {
    safeStorageSet(storageKey, state);
  }, [storageKey, state]);

  const currentTime = now();

  const recentFailures = useMemo(() => {
    return (state.failures || []).filter(
      (timestamp) => currentTime - timestamp < attemptWindowMs,
    );
  }, [state.failures, currentTime, attemptWindowMs]);

  const isLocked = state.lockedUntil > currentTime;
  const remainingLockMs = Math.max(0, state.lockedUntil - currentTime);

  const msSinceMount = currentTime - mountedAtRef.current;
  const isTooFast = msSinceMount < minFillTimeMs;
  const honeypotFilled = honeypot.trim().length > 0;

  const refreshState = useCallback(() => {
    const fresh = safeStorageGet(storageKey, {
      failures: [],
      lockedUntil: 0,
    });

    const cleaned = {
      failures: (fresh.failures || []).filter(
        (timestamp) => now() - timestamp < attemptWindowMs,
      ),
      lockedUntil: fresh.lockedUntil || 0,
    };

    setState(cleaned);
    return cleaned;
  }, [attemptWindowMs, storageKey]);

  const recordFailure = useCallback(
    (reason = "failed") => {
      const ts = now();
      const current = refreshState();
      const nextFailures = [...(current.failures || []), ts].filter(
        (timestamp) => ts - timestamp < attemptWindowMs,
      );

      const shouldLock = nextFailures.length >= maxFailures;
      const nextState = {
        failures: nextFailures,
        lockedUntil: shouldLock ? ts + lockoutMs : current.lockedUntil || 0,
        lastReason: reason,
      };

      setState(nextState);
      safeStorageSet(storageKey, nextState);

      return {
        ok: false,
        reason: shouldLock ? "locked" : reason,
        lockedUntil: nextState.lockedUntil,
        failures: nextFailures.length,
      };
    },
    [attemptWindowMs, lockoutMs, maxFailures, refreshState, storageKey],
  );

  const clearFailures = useCallback(() => {
    const nextState = {
      failures: [],
      lockedUntil: 0,
    };
    setState(nextState);
    safeStorageSet(storageKey, nextState);
  }, [storageKey]);

  const resetFormTimer = useCallback(() => {
    mountedAtRef.current = now();
    setHoneypot("");
    setSubmitCount(0);
  }, []);

  const validateBeforeSubmit = useCallback(() => {
    const current = refreshState();
    const ts = now();

    if ((current.lockedUntil || 0) > ts) {
      return {
        ok: false,
        reason: "locked",
        message: "Too many failed attempts. Please wait and try again.",
        remainingLockMs: current.lockedUntil - ts,
      };
    }

    if (honeypot.trim()) {
      recordFailure("honeypot");
      return {
        ok: false,
        reason: "honeypot",
        message: "Submission blocked.",
      };
    }

    if (ts - mountedAtRef.current < minFillTimeMs) {
      recordFailure("too_fast");
      return {
        ok: false,
        reason: "too_fast",
        message: "Form submitted too quickly. Please try again.",
      };
    }

    return {
      ok: true,
    };
  }, [honeypot, minFillTimeMs, recordFailure, refreshState]);

  const registerSubmit = useCallback(() => {
    setSubmitCount((count) => count + 1);
  }, []);

  return {
    honeypot,
    setHoneypot,
    submitCount,
    registerSubmit,
    validateBeforeSubmit,
    recordFailure,
    clearFailures,
    resetFormTimer,
    refreshState,
    isLocked,
    remainingLockMs,
    recentFailureCount: recentFailures.length,
    isTooFast,
    honeypotFilled,
    honeypotProps: {
      name: "website",
      value: honeypot,
      onChange: (e) => setHoneypot(e.target.value),
      tabIndex: -1,
      autoComplete: "off",
      "aria-hidden": true,
    },
  };
}
